/**
 * Read-only Temporal inspection calls (`/workflows/{workflowId}/…` on the
 * integration-service). Thin typed wrappers that unwrap the
 * `{ success, message, data }` envelope; masking happens one layer up in the
 * hooks so these stay a faithful view of the wire. Every route is
 * subsidiary-scoped by the client interceptors and 404s once the execution
 * ages out of Temporal retention.
 */
import { integrationsClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import type { ApiResponse } from '~/features/onboarding/types/connection.types';

import type {
  HistoryPageQuery,
  WorkflowChildrenResponse,
  WorkflowExecutionDetail,
  WorkflowHistoryPage,
  WorkflowQueryDefinitionsResponse,
  WorkflowQueryResult,
  WorkflowStackTraceResponse,
  WorkflowWorkersResponse,
} from '../types/workflow-inspection.types';

/** `runId` query param, omitted when inspecting the current run. */
function runParams(runId: string | null | undefined): Record<string, string> {
  return runId ? { runId } : {};
}

/** One page of event history. Pass `nextPageToken` back with the SAME `order`. */
export async function getWorkflowHistoryPage(
  workflowId: string,
  query: HistoryPageQuery = {},
): Promise<WorkflowHistoryPage> {
  const searchParams: Record<string, string | number> = { ...runParams(query.runId) };
  if (query.pageSize !== undefined) searchParams.pageSize = query.pageSize;
  if (query.nextPageToken) searchParams.nextPageToken = query.nextPageToken;
  if (query.afterEventId !== undefined && query.afterEventId !== null)
    searchParams.afterEventId = query.afterEventId;
  if (query.order) searchParams.order = query.order;
  if (query.eventFilter) searchParams.eventFilter = query.eventFilter;
  const res = await integrationsClient
    .get(endpoints.workflows.history(workflowId), { searchParams })
    .json<ApiResponse<WorkflowHistoryPage>>();
  return res.data;
}

/** Execution detail: input, result/failure, pending activities, attributes. */
export async function getWorkflowDescribe(
  workflowId: string,
  runId?: string | null,
): Promise<WorkflowExecutionDetail> {
  const res = await integrationsClient
    .get(endpoints.workflows.describe(workflowId), { searchParams: runParams(runId) })
    .json<ApiResponse<WorkflowExecutionDetail>>();
  return res.data;
}

/** Child workflows; `includeLiveStatus` re-describes still-open children server-side. */
export async function getWorkflowChildren(
  workflowId: string,
  runId?: string | null,
  includeLiveStatus = false,
): Promise<WorkflowChildrenResponse> {
  const res = await integrationsClient
    .get(endpoints.workflows.children(workflowId), {
      searchParams: { ...runParams(runId), includeLiveStatus: String(includeLiveStatus) },
    })
    .json<ApiResponse<WorkflowChildrenResponse>>();
  return res.data;
}

/** Pollers on the workflow's own task queue. */
export async function getWorkflowWorkers(
  workflowId: string,
  runId?: string | null,
): Promise<WorkflowWorkersResponse> {
  const res = await integrationsClient
    .get(endpoints.workflows.workers(workflowId), { searchParams: runParams(runId) })
    .json<ApiResponse<WorkflowWorkersResponse>>();
  return res.data;
}

/** Live call stack — 409 when the workflow isn't running, 503 when no worker answers. */
export async function getWorkflowStackTrace(
  workflowId: string,
  runId?: string | null,
): Promise<WorkflowStackTraceResponse> {
  const res = await integrationsClient
    .get(endpoints.workflows.stackTrace(workflowId), { searchParams: runParams(runId) })
    .json<ApiResponse<WorkflowStackTraceResponse>>();
  return res.data;
}

/** Query handlers the workflow declares (`__stack_trace` is always present). */
export async function listWorkflowQueries(
  workflowId: string,
  runId?: string | null,
): Promise<WorkflowQueryDefinitionsResponse> {
  const res = await integrationsClient
    .get(endpoints.workflows.queries(workflowId), { searchParams: runParams(runId) })
    .json<ApiResponse<WorkflowQueryDefinitionsResponse>>();
  return res.data;
}

/** Run one query with positional `args` (≤ 20, body ≤ 64 KiB; server timeout 10 s). */
export async function runWorkflowQuery(
  workflowId: string,
  queryName: string,
  args: unknown[],
  runId?: string | null,
): Promise<WorkflowQueryResult> {
  const res = await integrationsClient
    .post(endpoints.workflows.query(workflowId, queryName), {
      searchParams: runParams(runId),
      json: { args },
    })
    .json<ApiResponse<WorkflowQueryResult>>();
  return res.data;
}
